import React, { useState, useEffect, useContext, createContext } from 'react';
import { app } from '@BUP/configs/Firebase';
import { BupLoading } from '@BUP/components';
import { notification } from 'antd';
import {
	firebaseSignIn,
	firebaseSignUp,
	firebaseSignInProvider,
	firebaseSignOut,
	firebaseSendPasswordResetEmail,
	firebaseConfirmPasswordReset,
	firebaseGetCurrentUserToken,
	firebaseRestorePassword
} from '@BUP/services/Firebase';
import {
	getUserByFirebaseID,
	createUserByInvitationCode,
	createUserWhitOutInvitationCode
} from '@BUP/services/BraveUp';
import { BraveBupAPI } from '@BUP/services/Axios';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const { isLoading, ...auth } = useProvideAuth();
	const [isMainLoading, setIsMainLoading] = useState(true);

	useEffect(() => {
		setTimeout(() => setIsMainLoading(false), 1000);
	}, []);

	if (isMainLoading || isLoading) return <BupLoading />

	return (
		<AuthContext.Provider
			value={auth}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	return useContext(AuthContext);
};

function useProvideAuth() {
	const [user, setUser] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const signIn = async (email, password) => {
		try {
			const { user } = await firebaseSignIn(email, password);
		} catch (e) {
			throw e;
		}
	};

	const signUp = async (email, password, name, code) => {
		try {
			const { user } = await firebaseSignUp(email, password);
			const newUser = await getOrCreateUser(user, { name: 'email', data: { name, code } });
			setUser(newUser);
		} catch (e) {
			throw e;
		}
	};

	const signInProvider = async (provider) => {
		try {
			const { user } = await firebaseSignInProvider(provider);
			const newUser = await getOrCreateUser(user, { name: 'provider', data: {} });
			setUser(newUser);
		} catch (e) {
			throw e;
		}
	};

	const signOut = async () => {
		try {
			await firebaseSignOut();
			setUser(null);
		} catch (e) {
			throw e;
		}
	};

	const sendPasswordResetEmail = async (email) => {
		try {
			await firebaseSendPasswordResetEmail(email);
		} catch (e) {
			throw e;
		}

	};

	const confirmPasswordReset = async (code, password) => {
		try {
			await firebaseConfirmPasswordReset(code, password);
		} catch (e) {
			throw e;
		}
	};

	const emailPasswordReset = async (email) => {
		try{
			await firebaseRestorePassword(email);
			notification["info"]({
				style: { fontWeight: 500, fontSize: 17 },
				message: "¡Mensaje Enviado!",
				description: `Se ha enviado un mensaje a ${email} para restablecer tu contraseña`,
				duration: 15,
			});
		}catch(e){
			throw e;
		}
	}

	const setRequestsToken = async (user) => {
		return new Promise(async (resolve, reject) => {
			const token = user && await firebaseGetCurrentUserToken();
			if (token) {
				BraveBupAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			} else {
				BraveBupAPI.defaults.headers.common['Authorization'] = null;
			}
			resolve();
		})
	}

	const getOrCreateUser = async (user, strategy) => {
		if (!user) return null;
		setIsLoading(true);
		try {
			await setRequestsToken(user);
			const braveUpUser = await getUserByFirebaseID(user);
			if (braveUpUser) {
				//console.log("hay braveUpUser", braveUpUser)
				return braveUpUser;
			} else {
				//console.log("no hay braveUpUser", braveUpUser)
				if (strategy) {
					//console.log("se deberia crear")
					const { name, data } = strategy;
					switch (name) {
						case 'email':
							const { name, code } = data;
							await createUserByInvitationCode(user, name, code);
							//console.log("se creo usuario con codigo")
							break;
						default:
							await createUserWhitOutInvitationCode(user);
							//console.log("se creo usuario sin codigo");
							break;
					}
					//console.log("last phase")
					const newBraveUpUser = await getUserByFirebaseID(user);
					//console.log("last newBraveUpUser", newBraveUpUser)
					return newBraveUpUser;
				}
			}
		} catch (e) {
			console.log("getOrCreateUser error", e)
			notification['info']({
				style: { fontWeight: 500, fontSize: 17 },
				message: e.title || 'ERROR AL INICIAR SESIÓN',
				description: e.message || 'Ocurrio un error al iniciar sesíon, por favor vuelve a intentarlo.',
				duration: 10
			});
		} finally {
			setIsLoading(false);
		}
	}

	const setTokenAndUser = async (authUser) => {
		if (authUser) {
			//console.log("1 onAuthStateChanged", authUser)
			const user = await getOrCreateUser(authUser);
			//console.log("2 onAuthStateChanged user", user)
			setUser(user);
		} else {
			setUser(null);
		}
	}

	useEffect(() => {
		const unsubscribe = app.auth().onAuthStateChanged(setTokenAndUser);
		return () => unsubscribe();
	}, []);

	return {
		isLoading,
		user,
		signIn,
		signUp,
		signOut,
		signInProvider,
		sendPasswordResetEmail,
		confirmPasswordReset,
		emailPasswordReset
	};
}